import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { TextField } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { useApplyPromoCodeMutation } from '../../services/cartApi';
import {
  resetPromoCodeInputState,
  selectShowSuccess,
  useClearPromoCodeError,
} from '../../slices/promoCode';
import { desktop } from '../../styles';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import { Component as SpinningLoader } from '../SpinningLoader';

import { CartDrawerPromoCodeAppliedCodes } from './CartDrawerPromoCodeAppliedCodes';
import { CartDrawerPromoCodeSubtext } from './CartDrawerPromoCodeSubtext';
import { PromoCodeSuccessIconRenderer } from './CartDrawerPromoCodeSuccessIcon';
import { useOpenModalOnUnauthorizedPromoCodeAttempt } from './useOpenModalOnUnauthorizedPromoCodeAttempt';
import { usePromoCodeInputSubtextStyling } from './usePromoCodeInputSubtextStyling';

const SHOW_APPLIED_CODES = false;
const CodeInput = styled(TextField)`
  width: 100%;
  margin-right: ${({ theme }) => theme.spacing.tiny}px;
`;

const InputForm = styled.form`
  position: relative;

  ${desktop`
    margin-bottom: ${props => props.theme.spacing.tiny}px;
  `}
`;

const InputWrapper = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: ${props => props.theme.spacing.tiny}px;
`;

const StyledApplyButton = styled.button`
  position: relative;
  ${props => props.theme.button.defaultStyle};
  ${props => props.theme.button.primary};
  min-width: 94px;
  border-color: ${props => props.theme.colors.grey};
  height: 50px;
  outline: none;

  &:focus,
  &:active,
  &:hover,
  &:visited {
    outline: none;
  }

  &::-moz-focus-inner {
    border: 0;
  }
`;
const LabelWrapper = styled.div`
  > span {
    white-space: nowrap;
  }
`;

export const CartDrawerPromoCode = () => {
  const [applyPromoCode, { isLoading }] = useApplyPromoCodeMutation();
  const showSuccess = useSelector(selectShowSuccess);
  const [promoCode, setPromoCode] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const clearInputError = useClearPromoCodeError();

  useEffect(() => {
    clearInputError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFocus = useCallback(() => {
    clearInputError();
    setIsInputFocused(true);
  }, [clearInputError, setIsInputFocused]);

  const handleBlur = useCallback(
    () => setIsInputFocused(false),
    [setIsInputFocused]
  );
  const handleChange = useCallback(
    event => {
      setPromoCode(event.target.value);
    },
    [setPromoCode]
  );

  const dispatch = useDispatch();
  const handleResetInput = useCallback(() => {
    setPromoCode('');
    dispatch(resetPromoCodeInputState());
  }, [setPromoCode, dispatch]);

  const {
    subtextStyling,
    inputPromoCodeStyle,
    labelPromoCodeStyle,
    inputBoxStyle,
  } = usePromoCodeInputSubtextStyling();
  const { 'gift-cards': isGiftCardsEnabled } = useLDFlags();
  useOpenModalOnUnauthorizedPromoCodeAttempt();

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      applyPromoCode({ promoCode });
    },
    [applyPromoCode, promoCode]
  );

  return (
    <>
      <InputForm onSubmit={handleSubmit}>
        <InputWrapper>
          <PromoCodeSuccessIconRenderer handleResetInput={handleResetInput} />
          <CodeInput
            label={
              isInputFocused || promoCode.length > 0 ? (
                <FormattedMessage
                  id="global_checkout.enter_code_label"
                  defaultMessage="Enter code"
                />
              ) : (
                <LabelWrapper>
                  {isGiftCardsEnabled ? (
                    <FormattedMessage
                      id="global_checkout.add_promo_code_label"
                      defaultMessage="Add Promo code"
                    />
                  ) : (
                    <FormattedMessage
                      id="global_checkout.promo_code_label"
                      defaultMessage="Promo Code"
                    />
                  )}
                </LabelWrapper>
              )
            }
            error={<CartDrawerPromoCodeSubtext />}
            errorStyle={subtextStyling}
            value={promoCode}
            onChange={handleChange}
            inputStyle={inputPromoCodeStyle}
            labelStyle={labelPromoCodeStyle}
            inputBoxStyle={inputBoxStyle}
            onFocus={handleFocus}
            onBlur={handleBlur}
            showSuccess={showSuccess}
            isError
            variant="updatedPromoCodeField"
          />
          <StyledApplyButton
            disabled={promoCode.trim().length < 1 || isLoading}
            data-autotag="apply-coupon-button"
            data-qa-automation="ApplyCouponButton"
            type="submit"
          >
            {isLoading ? (
              <SpinningLoader color="white" isLoading />
            ) : (
              <FormattedMessage
                id="global_checkout.apply"
                defaultMessage="Apply"
              />
            )}
          </StyledApplyButton>
        </InputWrapper>
      </InputForm>
      {SHOW_APPLIED_CODES && <CartDrawerPromoCodeAppliedCodes />}
    </>
  );
};
